import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { debounce, isEmpty, isNil } from 'lodash'

import { searchConnectionItems } from '@/reducers/organizations/documentSourceConnection.actions'
import { syncedRootsOf } from '@/views/Organizations/components/ManageFilesModal.helpers'

export const SEARCH_DEBOUNCE_MS = 300
const MIN_SEARCH_LENGTH = 2

const searchRootOf = (root) =>
  isNil(root.rootId) ? { drive_id: root.driveId } : { drive_id: root.driveId, root_id: root.rootId }

/**
 * The Manage files search box: what was typed, its debounced form, and the search for it.
 * Only synced roots are searched; with none, nothing is sent.
 *
 * @param {number} props.connectionId the document source connection to search
 * @param {ManageFilesRoot[]} props.roots the modal's top-level nodes (typedef in
 *   ManageFilesModal.helpers.js)
 * @param {Object[]|null} props.drives the connection's drives; always loaded once the search box shows
 * @param {number} props.treeVersion bumped when a save changes the server's state; re-runs the search
 * @returns {{query: string, setQuery: Function, debouncedQuery: string, inSearchMode: boolean,
 *   searchResult: Object|null}} `searchResult` is the thunk's `{ data }` or `{ error }` for the
 *   current query and tree version, and null while that search runs
 */
export const useManageFilesSearch = ({ connectionId, roots, drives, treeVersion }) => {
  const dispatch = useDispatch()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  // Tagged with the query and tree version it answers, so an older search never shows as current.
  const [searchResult, setSearchResult] = useState(null)

  useEffect(() => {
    const debounced = debounce(setDebouncedQuery, SEARCH_DEBOUNCE_MS)
    debounced(query.trim())
    return () => debounced.cancel()
  }, [query])

  const inSearchMode = debouncedQuery.length >= MIN_SEARCH_LENGTH

  useEffect(() => {
    if (!inSearchMode) {
      return undefined
    }
    // Unsynced drives have nothing to search; the modal says so instead.
    const syncedRoots = syncedRootsOf(roots, drives)
    if (isEmpty(syncedRoots)) {
      return undefined
    }

    let current = true
    dispatch(
      searchConnectionItems({
        connectionId,
        query: debouncedQuery,
        roots: syncedRoots.map(searchRootOf),
      })
    ).then((result) => {
      if (current) {
        setSearchResult({ query: debouncedQuery, version: treeVersion, ...result })
      }
    })
    return () => {
      current = false
    }
  }, [debouncedQuery, treeVersion])

  const answersCurrent =
    searchResult?.query === debouncedQuery && searchResult?.version === treeVersion

  return {
    query,
    setQuery,
    debouncedQuery,
    inSearchMode,
    searchResult: answersCurrent ? searchResult : null,
  }
}
